import { BarChartOutlined } from '@ant-design/icons';
import { Dropdown } from 'antd';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Popover } from 'components/ui';
import { type AtlasGqlImageFilter, useGetImageFiltersQuery } from 'types/atlas-graphql';

import { type ImageProps } from '../Image';
import { StyledButton } from '../ImageViewer.style';

import { Levels } from './Levels';

const NO_FILTER = 'no-filter';
const ORIGINAL = 'original';
const LEVELS = 'levels';

export type FiltersProps = {
  onChange: (data: any) => void;
  onClickLevels: () => void;
  value?: string;
  levels?: { a?: number; b?: number };
  pictureId?: string;
  pictureGlobalId?: string;
  showLevels?: boolean;
  refetch: ImageProps['refetch'];
};

export const Filters: React.FunctionComponent<FiltersProps> = props => {
  const { onChange, onClickLevels, showLevels = true, value = NO_FILTER } = props;

  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [levelsOpen, setLevelsOpen] = useState<boolean>(false);
  const [hasUnsavedLevels, setHasUnsavedLevels] = useState<boolean>(false);

  const { data, loading: imageFiltersLoading } = useGetImageFiltersQuery();
  const imageFilters: AtlasGqlImageFilter[] = (data?.imageFilters ?? []).filter(
    (filter): filter is AtlasGqlImageFilter => {
      // strip original option
      if (filter?.id === ORIGINAL) {
        return false;
      }

      return !!filter;
    }
  );

  const defaultImageFilter: AtlasGqlImageFilter = {
    id: NO_FILTER,
    label: t('damage.noFilter', 'No Filter'),
  };

  const handleLevelsToggle = () => {
    setLevelsOpen(open => !open);
  };

  const handleLevelsChange = (isChanged: boolean) => {
    setHasUnsavedLevels(isChanged);
  };

  const levelsFilter = {
    id: LEVELS,
    label: (
      <Popover
        content={
          <Levels
            {...props}
            menuOpen={menuOpen}
            onLevelsToggle={handleLevelsToggle}
            onLevelsChange={handleLevelsChange}
          />
        }
        open={levelsOpen}
        placement="leftTop"
        trigger="click"
        arrow={false}
      >
        <span>{t('images.levels', 'Levels')}</span>
      </Popover>
    ),
  };

  const filterItems = [
    defaultImageFilter,
    ...imageFilters,
    ...(showLevels ? [levelsFilter] : []),
  ].map(filter => {
    const { id, label } = filter;
    return {
      key: id,
      label: label,
      disabled: id === value,
    };
  });

  const handleClick = useCallback(
    ({ key }) => {
      if (!key.includes(LEVELS)) {
        onChange({ filter: key === NO_FILTER ? null : key });
      } else if (key === LEVELS) {
        onClickLevels();
        handleLevelsToggle();
      }
    },
    [onChange, onClickLevels]
  );

  return (
    <Dropdown
      menu={{
        items: filterItems,
        onClick: handleClick,
      }}
      trigger={['click']}
      open={menuOpen}
      onOpenChange={(open: boolean, { source }) => {
        if (source === 'trigger') {
          if (!open) {
            setLevelsOpen(false);
          }
          setMenuOpen(open);
        }
      }}
    >
      <StyledButton
        data-testid="image-controls-filter"
        title={t('common.filter', 'Filter')}
        type={value !== NO_FILTER || hasUnsavedLevels ? 'primary' : 'default'}
        disabled={imageFiltersLoading}
      >
        <BarChartOutlined />
      </StyledButton>
    </Dropdown>
  );
};
